'use client';

import Link from '~/(tenant)/_components/tenant-link';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatLocaleDate } from '@kit/shared/utils';
import { Trans } from '@kit/ui/trans';

import { useMyMemberships } from '~/(tenant)/_components/native/_lib/use-my-memberships';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { SectionHeader } from '~/(tenant)/_components/native/_primitives';
import type { MyMembershipsSectionConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';

// Cross-segment import: the (tenant) layout group is shared, and other native
// sections (e.g. native-home-renderer's coaches/today-classes imports) follow
// the same pattern. NativeMembershipCard owns the editorial card design we
// want here; extracting it would just leave a thin re-export.
import { NativeMembershipCard } from '~/(tenant)/memberships/_components/native-membership-card';
// `import type` from a `'server-only'` module is safe — TypeScript erases
// type-only imports before bundling, so the server-only guard never runs.
import type { MyMembershipRow } from '~/(tenant)/memberships/_lib/server/memberships-page.loader';

import type { MembershipStatus } from './member-id-card-section';
import { MemberStatusBadge } from './_member-id-card/rotating-qr-card';

interface MyMembershipsSectionProps {
  config: MyMembershipsSectionConfig;
  accountId: string;
}

/**
 * Glanceable equivalent of the /memberships Active tab.
 *
 * Hidden when:
 *   - the user is signed out
 *   - the user has no membership at this tenant
 *   - the only membership is cancelled (matches the member-id-card rule)
 */
export function MyMembershipsSection({
  config,
  accountId,
}: MyMembershipsSectionProps) {
  const { userId, memberships } = useMyMemberships(accountId);

  if (!userId) return null;

  const visible = (memberships ?? []).filter((m) => m.status !== 'cancelled');
  if (visible.length === 0) return null;

  const titleKey =
    visible.length === 1
      ? 'common:nativeHome.myMemberships.title'
      : 'common:nativeHome.myMemberships.titlePlural';
  const titleDefault = visible.length === 1 ? 'Your card' : 'Your cards';

  return (
    <MyMembershipsLayout
      memberships={visible}
      compact={config.variant === 'compact'}
      titleKey={titleKey}
      titleDefault={titleDefault}
    />
  );
}

function MyMembershipsLayout({
  memberships,
  compact,
  titleKey,
  titleDefault,
}: {
  memberships: MyMembershipRow[];
  compact: boolean;
  titleKey: string;
  titleDefault: string;
}) {
  const t = useNativeTokens();

  return (
    <section style={{ paddingTop: 32 }} data-test="native-my-memberships">
      <SectionHeader
        eyebrow={
          compact ? undefined : (
            <Trans
              i18nKey="common:nativeHome.myMemberships.eyebrow"
              defaults="Membership"
            />
          )
        }
        title={<Trans i18nKey={titleKey} defaults={titleDefault} />}
        action={{
          label: (
            <Trans
              i18nKey="common:nativeHome.myMemberships.manage"
              defaults="Manage"
            />
          ),
          href: '/memberships',
        }}
      />

      {compact ? (
        <div
          style={{
            marginTop: 14,
            marginInline: 16,
            borderRadius: t.radiusLg,
            border: `1px solid ${t.line}`,
            backgroundColor: t.surface,
            overflow: 'hidden',
          }}
        >
          {memberships.map((membership, index) => (
            <CompactMembershipRow
              key={membership.id}
              membership={membership}
              showDivider={index > 0}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            marginTop: 18,
            paddingInline: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {memberships.map((membership) => (
            <NativeMembershipCard
              key={membership.id}
              membership={membership}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CompactMembershipRow({
  membership,
  showDivider,
}: {
  membership: MyMembershipRow;
  showDivider: boolean;
}) {
  const t = useNativeTokens();
  const { t: translate, i18n } = useTranslation();
  const status = membership.status as Exclude<MembershipStatus, 'cancelled'>;

  const subtitle = [
    membership.is_child ? membership.member_name : null,
    membership.expires_at
      ? translate('common:nativeHome.myMemberships.until', {
          defaultValue: 'Until {{date}}',
          date: formatLocaleDate(membership.expires_at, i18n.language),
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      href={`/memberships/${membership.id}`}
      data-test="native-compact-membership"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        borderTop: showDivider ? `1px solid ${t.line}` : undefined,
        color: t.foreground,
        textDecoration: 'none',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 10,
          height: 10,
          flexShrink: 0,
          borderRadius: 999,
          backgroundColor: membership.plan_color || t.accent,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: t.fontSans,
            fontSize: 15,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {membership.plan_name}
        </div>
        {subtitle ? (
          <div
            style={{
              fontFamily: t.fontSans,
              fontSize: 12,
              color: t.muted,
              marginTop: 2,
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>
      <MemberStatusBadge status={status} />
      <ChevronRight
        size={16}
        color={t.muted}
        className="shrink-0 rtl:rotate-180"
        aria-hidden
      />
    </Link>
  );
}
